import { forwardRef } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * Button with built-in variants and loading state.
 *
 * Usage:
 *   <Button>Save</Button>
 *   <Button variant="secondary">Cancel</Button>
 *   <Button variant="danger" onClick={handleDelete}>Delete</Button>
 *   <Button loading>Saving…</Button>
 */
export const Button = forwardRef(function Button({
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    className,
    children,
    type = 'button',
    ...props
}, ref) {
    const variantClasses = {
        primary: 'bg-ink text-paper hover:bg-ink-soft',
        secondary: 'border border-paper-border bg-paper-card text-ink hover:bg-paper-hover',
        ghost: 'text-ink-muted hover:bg-paper-hover hover:text-ink',
        danger: 'bg-status-danger text-paper-card hover:bg-red-800',
    };
    const sizeClasses = {
        sm: 'px-2.5 py-1.5 text-xs rounded-md',
        md: 'px-3.5 py-2 text-sm rounded-md',
        lg: 'px-4 py-2.5 text-base rounded-md',
    };

    return (
        <button
            ref={ref}
            type={type}
            disabled={disabled || loading}
            className={cn(
                'inline-flex items-center justify-center gap-2 font-medium transition-colors',
                'disabled:pointer-events-none disabled:opacity-50',
                variantClasses[variant],
                sizeClasses[size],
                className,
            )}
            {...props}
        >
            {loading && (
                <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" className="opacity-75" />
                </svg>
            )}
            {children}
        </button>
    );
});
